import { STEFAN_BOLTZMANN, SOLAR_LUMINOSITY, AU } from '../constants';

/**
 * engines/habitability
 *
 * ハビタブルゾーン（生命居住可能領域）・惑星の平衡温度・ドレイクの方程式を扱う。
 *
 * 【この engine だけ性質が異なることに注意】
 * engines/orbital（ニュートン力学）や engines/relativity（一般相対性理論）は、
 * 実測値と比較して「正しいかどうか」を検証できる。しかし、生命の起源確率(f_l)や
 * 文明の存続期間(L)といったドレイクの方程式の各項は、地球外生命の観測例が
 * ゼロ件である以上、原理的に実測値と照合できない。これはバグでも近似の粗さでも
 * なく、「フェルミのパラドックス」そのものの核心（なぜ我々は誰にも出会っていないのか、
 * を推定する変数自体が推定できない）である。
 * したがって driveEquationN() のテストは「入力に対して正しく掛け算しているか」
 * という数学的な検証に留め、"実測値との誤差" という評価軸は使わない。
 */

// ============================================================
// ハビタブルゾーン
// ============================================================

export interface HabitableZone {
  /** HZ内側境界 [AU] */
  innerAU: number;
  /** HZ外側境界 [AU] */
  outerAU: number;
}

/**
 * ハビタブルゾーンの境界を求める簡易スケーリング則。
 * 太陽(L=1)を基準に、境界が光度の平方根でスケールするという近似
 * (地球型惑星向けの教科書レベルの簡易モデル。Kopparapu et al. のような
 * より精密なモデルは恒星スペクトル型ごとに係数が変わるが、本バージョンでは
 * 簡易版として単一の係数を使う)。
 *
 * innerAU = 0.95 √(L/L☉)
 * outerAU = 1.67 √(L/L☉)
 */
export function habitableZone(luminositySolar: number): HabitableZone {
  if (luminositySolar <= 0) throw new Error('luminositySolar must be positive');
  const sqrtL = Math.sqrt(luminositySolar);
  return { innerAU: 0.95 * sqrtL, outerAU: 1.67 * sqrtL };
}

/** 指定した距離[AU]の惑星がハビタブルゾーン内にあるかどうかを判定する。 */
export function isInHabitableZone(distanceAU: number, luminositySolar: number): boolean {
  const { innerAU, outerAU } = habitableZone(luminositySolar);
  return distanceAU >= innerAU && distanceAU <= outerAU;
}

// ============================================================
// 平衡温度
// ============================================================

/**
 * 惑星の平衡温度 [K]（大気による温室効果を考慮しない、放射平衡のみの理論値）。
 * T_eq = [ (1-A) L / (16 π σ d²) ]^(1/4)
 *
 * @param luminosityW 恒星光度 [W]
 * @param distanceM   恒星からの距離 [m]
 * @param bondAlbedo  ボンドアルベド（惑星表面の反射率, 0〜1）
 *
 * 地球の場合(L=1L☉, d=1AU, A=0.3)、平衡温度は約255Kになる。実際の地表平均気温
 * (約288K)との差33Kが温室効果の大きさであり、この乖離自体が教育的に重要な数値。
 */
export function equilibriumTemperatureK(luminosityW: number, distanceM: number, bondAlbedo: number): number {
  if (luminosityW <= 0) throw new Error('luminosityW must be positive');
  if (distanceM <= 0) throw new Error('distanceM must be positive');
  if (bondAlbedo < 0 || bondAlbedo >= 1) throw new Error('bondAlbedo must be in [0, 1)');
  const numerator = (1 - bondAlbedo) * luminosityW;
  const denominator = 16 * Math.PI * STEFAN_BOLTZMANN * distanceM * distanceM;
  return Math.pow(numerator / denominator, 0.25);
}

/** 太陽光度単位・AU単位で使える便宜関数。 */
export function equilibriumTemperatureFromSolarUnits(
  luminositySolar: number,
  distanceAU: number,
  bondAlbedo = 0.3,
): number {
  const luminosityW = luminositySolar * SOLAR_LUMINOSITY;
  const distanceM = distanceAU * AU;
  return equilibriumTemperatureK(luminosityW, distanceM, bondAlbedo);
}

// ============================================================
// ドレイクの方程式
// ============================================================

export interface DrakeEquationParams {
  /** 銀河系内で1年あたりに形成される恒星の数 */
  starFormationRate: number;
  /** 惑星を持つ恒星の割合 [0,1] */
  fractionWithPlanets: number;
  /** 1恒星系あたりの生命居住可能な惑星の平均数 */
  habitablePlanetsPerSystem: number;
  /** 実際に生命が発生する割合 [0,1] */
  fractionDevelopingLife: number;
  /** その中で知的生命に進化する割合 [0,1] */
  fractionDevelopingIntelligence: number;
  /** その中で検出可能な信号を送る文明の割合 [0,1] */
  fractionDetectable: number;
  /** そのような文明が信号を送り続ける期間 [年] */
  civilizationLifetimeYears: number;
}

/**
 * ドレイクの方程式: 現在、天の川銀河内で交信可能な文明の推定数 N。
 * N = R* × f_p × n_e × f_l × f_i × f_c × L
 *
 * 【重要】この関数はパラメータを正しく掛け算しているだけであり、その計算結果が
 * 「正しい」ことを意味しない。各パラメータ自体が推定不能（既知の地球外生命の
 * 実例がゼロ件）だからこそフェルミのパラドックスが成立する。UIで複数のシナリオ
 * (楽観的/悲観的/保守的)を切り替えて、Nが何桁も変わることを見せるのが目的。
 */
export function driveEquationN(params: DrakeEquationParams): number {
  const {
    starFormationRate,
    fractionWithPlanets,
    habitablePlanetsPerSystem,
    fractionDevelopingLife,
    fractionDevelopingIntelligence,
    fractionDetectable,
    civilizationLifetimeYears,
  } = params;
  return (
    starFormationRate *
    fractionWithPlanets *
    habitablePlanetsPerSystem *
    fractionDevelopingLife *
    fractionDevelopingIntelligence *
    fractionDetectable *
    civilizationLifetimeYears
  );
}

/**
 * 文明数 N から、銀河円盤内での文明間の平均距離を大まかに見積もる。
 * 銀河円盤を半径 galaxyRadiusLy の一様な円盤とみなし、1文明あたりの
 * 占有面積から代表的な距離を概算する（あくまでオーダーエスティメイト）。
 *
 * separation ≈ √(銀河円盤の面積 / N)
 */
export function averageCivilizationSeparationLy(numberOfCivilizations: number, galaxyRadiusLy = 50000): number {
  if (numberOfCivilizations <= 0) return Infinity;
  const diskArea = Math.PI * galaxyRadiusLy * galaxyRadiusLy;
  return Math.sqrt(diskArea / numberOfCivilizations);
}
